import { useEffect, useMemo, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { WbText } from '@workbench/react';
import { useTranslation } from 'react-i18next';

import _notification from '../../../Utils/Vimdt/notification';
import VimdtButton from '../Common/VimdtButton';
import VimdtAccordion from '../Common/VimdtAccordion';
import CollapseAllToggle from '../Common/CollapseAllToggle';
import EquipmentRenderList from './EquipmentRenderList';
import EquipmentCategoryList from './EquipmentCategoryList';
import EquipmentFilter from './EquipmentFilter';
import './EquipmentList.scss';

const CATEGORIES = [
	{ key: '1', category: 'ENGINE', labelKey: 'engineComponent', countKey: 'engine' },
	{ key: '2', category: 'TRIM', labelKey: 'trimComponent', countKey: 'trim' },
	{ key: '3', category: 'RIM', labelKey: 'rimComponent', countKey: 'rim' },
	{ key: '4', category: 'UPHOLSTERY', labelKey: 'upholsteryComponent', countKey: 'upholstery' },
	{ key: '5', category: 'PAINT', labelKey: 'paintComponent', countKey: 'paint1' },
	{ key: '6', category: 'PAINT2', labelKey: 'upperPaintComponent', countKey: 'paint2' },
];

const EQUIPMENT_PANEL_KEY = '7';

const EquipmentList = ({
	imageList,
	equipmentList,
	isImagesLoading,
	saveFileName,
	rowCount = 6,
	disabled,
}) => {
	const { t } = useTranslation();

	const [imageFilter, setImageFilter] = useState(['']);
	const [equipmentFilter, setEquipmentFilter] = useState(['']);
	const [equipmentRenderList, setEquipmentRenderList] = useState([]);
	const [filter, setFilter] = useState(true);
	const [saving, setSaving] = useState(false);
	const [panels, setPanels] = useState([]);

 	const count =
 		imageList.length + equipmentList.reduce((total, group) => total + group.images.length, 0);
	
		const categoryLists = useMemo(
		() =>
			CATEGORIES.map((meta) => ({
				...meta,
				images: imageList.filter((image) => image.category === meta.category),
			})),
		[imageList]
	);

	const availablePanels = useMemo(() => {
		const keys = categoryLists.filter((c) => c.images.length > 0).map((c) => c.key);
		keys.push(EQUIPMENT_PANEL_KEY);
		return keys;
	}, [categoryLists]);

	const totalOptionCodeCount = useMemo(() => {
		if (imageList.length === 0) return 0;
		const categoryCount = categoryLists.reduce(
			(acc, c) => acc + (c.images.length > 0 ? 1 : 0),
			0
		);
		const equipmentCount =
			equipmentList.length > 0 && equipmentList[0].images.length > 0 ? equipmentList.length : 0;
		return categoryCount + equipmentCount;
	}, [imageList, equipmentList, categoryLists]);

	useEffect(() => {
		if (imageFilter[0] === '' && equipmentFilter[0] === '' && equipmentList.length > 0 && filter) {
			setEquipmentRenderList(equipmentList);
			return;
		}

		let tempList = JSON.parse(JSON.stringify(equipmentList));

		if (equipmentList.length > 0) {
			if (equipmentFilter[0] !== '') {
				tempList = tempList.filter((group) =>
 					equipmentFilter.some((code) => group.name.includes(code))
 				);
			}

			if (filter) {
				if (imageFilter[0] !== '') {
					tempList.forEach((group) => {
						group.images = group.images.filter((image) =>
							imageFilter.includes(image.resolution)
						);
					});
				}
			} else {
				tempList.forEach((group) => {
					group.images = [group.images[0]];
				});
			}

			if (tempList.length === 0) {
				tempList.push({ images: [], name: '' });
			}

			if (
				(tempList[0].name === '' || tempList[0].images.length === 0) &&
				!isImagesLoading
			) {
				_notification({
					title: t('vimdt.notification.warning'),
					message: t('vimdt.notification.notMatchingFilters'),
					duration: 10,
				});
			}
		}

		setEquipmentRenderList(tempList);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [equipmentFilter, imageFilter, equipmentList, filter]);

	const saveImages = () => {
		setSaving(true);
		const zip = new JSZip();
		const imageFolder = zip.folder(saveFileName);

		imageList.forEach(({ blob, fileName, imageType }) => {
			imageFolder.file(`${fileName}.${imageType}`, blob);
		});

		equipmentList.forEach((category) => {
			category.images.forEach(({ blob, fileName, imageType }) => {
				imageFolder.file(`${fileName}.${imageType}`, blob);
			});
		});

		zip
			.generateAsync({ type: 'blob' })
			.then((content) => {
 				saveAs(content, `${saveFileName}.zip`);
				setSaving(false);
			})
			.catch((err) => {
				console.error(err);
				setSaving(false);
			});
	};

	const panelHandler = (mode) => setPanels(mode === 0 ? availablePanels : []);

	const countText = `${count} ${
		count > 1 ? t('vimdt.infoTexts.imagesFound') : t('vimdt.infoTexts.imageFound')
	}`;
	const totalText = `${totalOptionCodeCount} ${
		totalOptionCodeCount > 1
			? t('vimdt.infoTexts.optionCodesFound')
			: t('vimdt.infoTexts.optionCodeFound')
	}`;

	const saveButton = (
		<VimdtButton
			label={t('vimdt.loadSaveButtons.saveImages')}
			filledIn={false}
			onClick={saveImages}
			loading={saving}
			disabled={disabled || isImagesLoading}
		/>
	);

	const accordionItems = [
		...categoryLists
			.filter((c) => c.images.length > 0)
			.map((c) => ({
				key: c.key,
				header: t(`vimdt.equipmentsTab.${c.labelKey}`),
				content: <EquipmentRenderList imageList={c.images} rowCount={rowCount} />,
			})),
		...(equipmentRenderList.length > 0
			? [
					{
						key: EQUIPMENT_PANEL_KEY,
						header: t('vimdt.equipmentsTab.equipmentComponent'),
						content: (
							<EquipmentCategoryList imageList={equipmentRenderList} rowCount={rowCount} />
						),
					},
			  ]
			: []),
	];

	return (
		<div className="vimdt-equipment-list">
			{imageList.length > 0 && (
				<>
					<div className="vimdt-equipment-list__header">
						<WbText strong>{`${countText} / ${totalText}`}</WbText>
						{saveButton}
					</div>
					<CollapseAllToggle
						panelHandler={panelHandler}
						panels={panels}
						availablePanels={availablePanels}
					/>
				</>
			)}

			{equipmentRenderList.length > 0 && (
				<EquipmentFilter
					equipmentFilterHandler={(equipment, image) => {
						setImageFilter(image);
						setEquipmentFilter(equipment);
					}}
					oneFilterHandler={setFilter}
				/>
			)}

			<VimdtAccordion items={accordionItems} activeKeys={panels} onChange={setPanels} />

			{imageList.length > 0 && <div className="vimdt-equipment-list__footer">{saveButton}</div>}
		</div>
	);
};

export default EquipmentList;
